import ChatMessage from "organisms/modals/chat-modal/chat-message";
import NewChat from "organisms/modals/chat-modal/new-chat";
import React, { useLayoutEffect, useMemo, useRef } from "react";
import SectionSpacer from "molecules/spacers/section-spacer";
import { AriaRole } from "utilities/enumerations/aria-role";
import { CollectionUtils } from "utilities/collection-utils";
import { SectionDividerType } from "models/enumerations/section-divider-type";
import { useConversation } from "utilities/atoms/conversations/current-conversation/use-conversation.hook";
import { useLocalization } from "utilities/hooks/use-localization";
import { MessageRole } from "models/enumerations/conversations/message-role";
import ConversationMessageRecord from "models/view-models/conversations/conversation-message-record";
import StringUtils from "utilities/string-utils";
import { useChatScroll } from "utilities/hooks/domain/chat/use-chat-scroll";
import { ConversationStateStatus } from "utilities/atoms/conversations/current-conversation/conversation-atom.reducer";
import ConversationApplicationContextRecord from "models/view-models/conversations/conversation-application-context-record";
import useFeatureFlags from "utilities/hooks/use-feature-flags";
import MessageParts from "./message-parts/message-parts";
import ToolCallSuggestions from "./tool-calls/tool-call-suggestions";
import SuggestMemoryPartRecord from "models/view-models/conversations/suggest-memory-part-record";

// -----------------------------------------------------------------------------------------
// #region Interfaces
// -----------------------------------------------------------------------------------------

export interface ChatModalResultsProps {
    onCitationClick?: () => void;

    /**
     * When true, the conversation id is passed down to the message and citation components. The id
     * is added as a query param to load this conversation in bookview when navigating to a citation reference.
     */
    includeConversationId?: boolean;
    containerRef: React.RefObject<HTMLDivElement>;
    isBookview: boolean;
    applicationContext?: ConversationApplicationContextRecord;
}

// #endregion Interfaces

// -----------------------------------------------------------------------------------------
// #region Constants
// -----------------------------------------------------------------------------------------

const BASE_CLASS = "c-chat-modal-results";

// #endregion Constants

// -----------------------------------------------------------------------------------------
// #region Component
// -----------------------------------------------------------------------------------------

const ChatModalResults: React.FC<ChatModalResultsProps> = React.memo(
    ({
        onCitationClick,
        containerRef,
        includeConversationId = false,
        isBookview,
        applicationContext,
    }) => {
        const { t } = useLocalization();
        const { useCasiContext } = useFeatureFlags();
        const { scrollToBottomOnLoad, scrollUserMessageToTop, messageRef } =
            useChatScroll(containerRef);
        const { currentConversation, retry, status } = useConversation({
            onConversationLoaded: scrollToBottomOnLoad,
        });
        const messages = useConversationMessagesFilter(
            currentConversation?.messages ?? []
        );

        const lastUserMessageIdRef = useRef<string | undefined>(undefined);

        const lastUserIndex = getLastUserIndex(messages);
        const lastUserMessage = messages.at(lastUserIndex);

        const isNewChat = CollectionUtils.isEmpty(messages);

        const [editingMessageId, setEditingMessageId] =
            React.useState<string>();

        useLayoutEffect(() => {
            if (!lastUserMessage) return;

            const shouldScroll =
                status === ConversationStateStatus.StreamingMessage &&
                lastUserMessage.id !== lastUserMessageIdRef.current;

            lastUserMessageIdRef.current = lastUserMessage.id;
            if (shouldScroll) {
                scrollUserMessageToTop();
            }
        }, [lastUserMessage, scrollUserMessageToTop, status]);

        useLayoutEffect(() => {
            scrollToBottomOnLoad();
        }, [scrollToBottomOnLoad]);

        if (isNewChat) {
            return <NewChat isBookview={isBookview} />;
        }

        return (
            <div
                aria-label={t("chatModal-ChatResults")}
                className={BASE_CLASS}
                role={AriaRole.List}>
                <div className={`${BASE_CLASS}__container`}>
                    {messages.map((message, index) => {
                        const isLastUser = index === lastUserIndex;
                        const isEditing =
                            StringUtils.hasValue(message.id) &&
                            StringUtils.hasValue(editingMessageId) &&
                            message.id === editingMessageId;

                        return (
                            <MessageItem
                                index={index}
                                key={message.id ?? `message-${index}`}
                                message={message}
                                isEditing={isEditing}
                                isLastUser={isLastUser}
                                messageRef={messageRef}
                                onCitationClick={onCitationClick}
                                onRetryClick={(message) =>
                                    retry(
                                        message,
                                        useCasiContext
                                            ? applicationContext
                                            : undefined
                                    )
                                }
                                onEditMessage={setEditingMessageId}
                                applicationContext={
                                    useCasiContext
                                        ? applicationContext
                                        : undefined
                                }
                                conversationId={
                                    includeConversationId
                                        ? currentConversation?.id
                                        : undefined
                                }
                            />
                        );
                    })}
                </div>
            </div>
        );
    }
);

// #endregion Component

/**
 * Filters conversation messages to include only user messages, assistant messages with content,
 * and tool messages carrying parts. System messages are excluded.
 */
export function useConversationMessagesFilter(
    messages: ConversationMessageRecord[]
): ConversationMessageRecord[] {
    return useMemo(() => {
        return messages.filter((m) => {
            // Exclude system messages
            if (m.role === MessageRole.System) {
                return false;
            }

            // Include tool messages w/ parts (e.g., Memories)
            if (m.role === MessageRole.Tool) {
                return !CollectionUtils.isEmpty(m.parts);
            }

            if (m.role === MessageRole.User) {
                return true;
            }

            // Include assistant messages with content or displayable tool calls
            return (
                m.role === MessageRole.Assistant &&
                (StringUtils.hasValue(m.content) ||
                    m.toolCalls?.some((tc) => tc.shouldDisplay()) === true)
            );
        });
    }, [messages]);
}

// -----------------------------------------------------------------------------------------
// #region Exports
// -----------------------------------------------------------------------------------------

export default ChatModalResults;

// #endregion Exports

function getLastUserIndex(messages: ConversationMessageRecord[]): number {
    for (let i = messages.length - 1; i >= 0; i--) {
        if (messages[i].role === MessageRole.User) {
            return i;
        }
    }
    return -1;
}

type MemoizedMessageProps = {
    message: ConversationMessageRecord;
    isEditing?: boolean;
    isLastUser: boolean;
    messageRef?: React.RefObject<HTMLDivElement>;
    onCitationClick?: () => void;
    onRetryClick: (message: ConversationMessageRecord) => void;
    onEditMessage?: (id?: string) => void;
    conversationId?: string;
    applicationContext?: ConversationApplicationContextRecord;
    index: number;
};

const compareMessageProps = (
    prev: Readonly<MemoizedMessageProps>,
    next: Readonly<MemoizedMessageProps>
) =>
    prev.isEditing === next.isEditing &&
    prev.message.id === next.message.id &&
    prev.message.content === next.message.content &&
    prev.message.status === next.message.status &&
    prev.message.feedback === next.message.feedback &&
    prev.isLastUser === next.isLastUser &&
    prev.conversationId === next.conversationId &&
    prev.applicationContext === next.applicationContext &&
    CollectionUtils.equalsBy(
        (element) => element.id,
        prev.message.toolCalls,
        next.message.toolCalls
    ) &&
    CollectionUtils.equalsBy(
        (element) => element.status,
        prev.message.toolCalls,
        next.message.toolCalls
    ) &&
    CollectionUtils.equalsBy(
        (element) => element.partId,
        prev.message.parts,
        next.message.parts
    ) &&
    CollectionUtils.equalsBy(
        (element: SuggestMemoryPartRecord) => element.memory?.status,
        prev.message.parts,
        next.message.parts
    );

const MessageItem = React.memo<MemoizedMessageProps>(
    ({
        message,
        isEditing = false,
        isLastUser,
        messageRef,
        onCitationClick,
        onRetryClick,
        onEditMessage,
        conversationId,
        applicationContext,
    }) => {
        if (message.role === MessageRole.Tool) {
            return <MessageParts message={message} />;
        }
        if (CollectionUtils.hasValues(message.toolCalls)) {
            return (
                <ToolCallSuggestions
                    toolCalls={message.toolCalls}
                    messageId={message.id ?? ""}
                />
            );
        }
        return (
            <>
                <div ref={isLastUser ? messageRef : undefined}>
                    <ChatMessage
                        isEditing={isEditing}
                        message={message}
                        onCitationClick={onCitationClick}
                        onRetryClick={onRetryClick}
                        onEditMessage={onEditMessage}
                        conversationId={conversationId}
                        applicationContext={applicationContext}
                    />
                </div>
                <SectionSpacer type={SectionDividerType.Small} />
            </>
        );
    },
    compareMessageProps
);
